/**
 * Trunnion AI - Chat Message Bubble
 * Renders user and assistant messages with tool calls and reasoning.
 */

import { useState } from 'react';
import { User, Bot, ChevronDown, ChevronRight, Wrench, Brain } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import type { ChatMessage as ChatMessageType, ChatToolCall, ChatReasoningEntry } from '@/types/chat.types';

interface ChatMessageProps {
  message: ChatMessageType;
}

function ToolCallInline({ toolCall }: { toolCall: ChatToolCall }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor = {
    pending: 'text-yellow-400',
    running: 'text-blue-400',
    completed: 'text-green-400',
    failed: 'text-red-400',
  }[toolCall.status];

  return (
    <div className="my-2 rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800/80">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs"
      >
        <Wrench className={`h-3.5 w-3.5 ${statusColor}`} />
        <span className="font-medium text-trunnion-slate-200">{toolCall.toolName}</span>
        <Badge variant={toolCall.status === 'completed' ? 'success' : toolCall.status === 'failed' ? 'error' : 'info'}>
          {toolCall.status}
        </Badge>
        {toolCall.durationMs && (
          <span className="text-trunnion-slate-500">{toolCall.durationMs}ms</span>
        )}
        <span className="ml-auto">
          {expanded ? <ChevronDown className="h-3 w-3 text-trunnion-slate-500" /> : <ChevronRight className="h-3 w-3 text-trunnion-slate-500" />}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-trunnion-slate-700 px-3 py-2">
          <div className="mb-1 text-xs text-trunnion-slate-500">Parameters:</div>
          <pre className="text-xs text-trunnion-slate-300 font-mono overflow-x-auto">
            {JSON.stringify(toolCall.parameters, null, 2)}
          </pre>
          {toolCall.result != null && (
            <>
              <div className="mt-2 mb-1 text-xs text-trunnion-slate-500">Result:</div>
              <pre className="text-xs text-trunnion-slate-300 font-mono overflow-x-auto">
                {typeof toolCall.result === 'string'
                  ? toolCall.result
                  : JSON.stringify(toolCall.result, null, 2)}
              </pre>
            </>
          )}
          {toolCall.error && (
            <div className="mt-2 text-xs text-red-400">{toolCall.error}</div>
          )}
        </div>
      )}
    </div>
  );
}

function ReasoningAccordion({ entries }: { entries: ChatReasoningEntry[] }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="my-2 rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800/80">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs"
      >
        <Brain className="h-3.5 w-3.5 text-purple-400" />
        <span className="font-medium text-trunnion-slate-200">
          Reasoning ({entries.length} steps)
        </span>
        <span className="ml-auto">
          {expanded ? <ChevronDown className="h-3 w-3 text-trunnion-slate-500" /> : <ChevronRight className="h-3 w-3 text-trunnion-slate-500" />}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-trunnion-slate-700 px-3 py-2 space-y-2">
          {entries.map((entry) => (
            <div key={entry.id} className="text-xs">
              <span className="font-medium text-purple-300 capitalize">{entry.type}: </span>
              <span className="text-trunnion-slate-300">{entry.content}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ChatMessageBubble({ message }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
          isUser
            ? 'bg-trunnion-gold-500/20 text-trunnion-gold-500'
            : 'bg-blue-500/20 text-blue-400'
        }`}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>

      {/* Message content */}
      <div className={`max-w-[75%] ${isUser ? 'text-right' : ''}`}>
        <div
          className={`rounded-xl px-4 py-3 text-sm ${
            isUser
              ? 'bg-trunnion-gold-500/10 border border-trunnion-gold-500/20 text-white'
              : 'bg-trunnion-slate-800 border border-trunnion-slate-700 text-trunnion-slate-200'
          }`}
        >
          <div className="whitespace-pre-wrap">{message.content}</div>
        </div>

        {/* Tool calls */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-1">
            {message.toolCalls.map((tc) => (
              <ToolCallInline key={tc.id} toolCall={tc} />
            ))}
          </div>
        )}

        {/* Reasoning trace */}
        {message.reasoningTrace && message.reasoningTrace.length > 0 && (
          <div className="mt-1">
            <ReasoningAccordion entries={message.reasoningTrace} />
          </div>
        )}

        {/* Token usage */}
        {message.tokenUsage && (
          <div className="mt-1 text-xs text-trunnion-slate-500">
            {message.tokenUsage.totalTokens.toLocaleString()} tokens
            {message.tokenUsage.estimatedCost != null && (
              <span> &middot; ${message.tokenUsage.estimatedCost.toFixed(4)}</span>
            )}
          </div>
        )}

        {/* Timestamp */}
        <div className="mt-1 text-xs text-trunnion-slate-500">
          {new Date(message.createdAt).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
}
